import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MegaSection, MegaPromo, MegaLink } from "./megaNavConfig";
import MegaUtilityBar from "./MegaUtilityBar";
import { useLatestInsight } from "@/hooks/useLatestInsight";
import { useNextEvent } from "@/hooks/useNextEvent";

interface Props {
  items: MegaSection[];
  dark: boolean;
  isActive: (href: string) => boolean;
}

const LinkItem = ({ link }: { link: MegaLink }) => {
  const isExternal = /^(https?:|mailto:|tel:)/.test(link.href);
  const inner = (
    <>
      {link.icon && (
        <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary group-hover:bg-primary/15 transition-colors">
          <link.icon className="h-4 w-4" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1 text-[13px] font-semibold text-slate-900">
          {link.label}
          <ChevronRight className="h-3 w-3 opacity-0 -translate-x-1 transition-all group-hover:opacity-60 group-hover:translate-x-0" />
        </span>
        <span className="block text-[12px] text-slate-600 leading-snug mt-0.5">
          {link.description}
        </span>
      </span>
    </>
  );
  const className = "group flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-slate-100 transition-colors";
  if (isExternal) {
    return <a href={link.href} className={className}>{inner}</a>;
  }
  return <Link to={link.href} className={className}>{inner}</Link>;
};

const PromoCard = ({ promo }: { promo: MegaPromo }) => (
  <Link
    to={promo.href}
    className="group relative flex flex-col justify-between overflow-hidden rounded-card border border-slate-200 bg-gradient-to-br from-primary/10 via-slate-50 to-transparent p-4 min-h-[165px] hover:border-primary/40 transition-colors"
  >
    <div>
      <span className="inline-block text-[10px] uppercase tracking-eyebrow text-primary font-semibold">
        {promo.eyebrow}
      </span>
      <h4 className="mt-2 font-display text-[18px] leading-tight text-slate-900">
        {promo.title}
      </h4>
      <p className="mt-2 text-[12px] text-slate-600 leading-snug">{promo.body}</p>
    </div>
    <span className="mt-4 inline-flex items-center gap-1 text-[12px] font-medium text-primary">
      {promo.cta}
      <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </span>
  </Link>
);

const PanelContent = ({ section }: { section: MegaSection }) => {
  const [activeIndustry, setActiveIndustry] = useState(0);
  const isInsights = section.label === "Insights";
  const isCompany = section.label === "Company";
  const { data: latest } = useLatestInsight({ kinds: isInsights ? ["blog", "webinar"] : undefined });
  const { data: nextEvent } = useNextEvent();
  let promo = section.industries ? section.industries[activeIndustry].promo : section.promo;
  if (isInsights && latest) {
    promo = {
      eyebrow: `Latest · ${latest.kindLabel}`,
      title: latest.title,
      body: section.promo.body,
      href: latest.href,
      cta: "Read now",
    };
  }
  if (isCompany && nextEvent) {
    const where = nextEvent.isVirtual ? "Virtual" : nextEvent.location;
    const meta = [nextEvent.dateLabel, where].filter(Boolean).join(" · ");
    promo = {
      eyebrow: `Next event · ${meta}`,
      title: nextEvent.title,
      body: section.promo.body,
      href: nextEvent.href,
      cta: "View event",
    };
  }

  return (
    <div className="w-[calc(100vw-32px)] sm:w-[calc(100vw-48px)] lg:w-[min(1080px,calc(100vw-64px))] overflow-hidden rounded-card border border-slate-200 bg-white shadow-2xl shadow-slate-900/20">
      <div className="grid grid-cols-12 gap-5 p-5 min-h-[270px] content-start">
        {section.industries && (
          <div className="col-span-3 border-r border-slate-200 pr-4">
            <p className="mb-2 px-2 text-[10px] uppercase tracking-eyebrow text-slate-500 font-semibold">
              Industries
            </p>
            <ul className="space-y-0.5">
              {section.industries.map((ind, i) => (
                <li key={ind.label}>
                  <button
                    type="button"
                    onMouseEnter={() => setActiveIndustry(i)}
                    onFocus={() => setActiveIndustry(i)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-[13px] transition-colors",
                      i === activeIndustry
                        ? "bg-primary/10 text-slate-900 font-semibold"
                        : "text-slate-700 hover:bg-slate-100",
                    )}
                  >
                    {ind.label}
                    <ChevronRight className="h-3 w-3 opacity-50" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={cn(section.industries ? "col-span-5" : "col-span-8")}>
          {section.groups.map((g, gi) => (
            <div key={g.heading ?? gi} className="mb-2 last:mb-0">
              {g.heading && (
                <p className="mb-1 px-3 text-[10px] uppercase tracking-eyebrow text-slate-500 font-semibold">
                  {g.heading}
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {g.links.map((l) => (
                  <LinkItem key={l.href} link={l} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="col-span-4">
          <PromoCard promo={promo} />
        </div>
      </div>
      <MegaUtilityBar />
    </div>
  );
};

const CLOSE_DELAY = 150;

const MegaMenu = ({ items, dark, isActive }: Props) => {
  const triggerRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const closeTimer = useRef<number | null>(null);
  const { pathname } = useLocation();

  const activeSection = items.find((i) => i.href === activeMenu) ?? null;

  // Close on route change
  useEffect(() => {
    setActiveMenu(null);
  }, [pathname]);

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setActiveMenu(null), CLOSE_DELAY);
  };
  const openMenu = (key: string) => {
    cancelClose();
    setActiveMenu(key);
  };

  useEffect(() => () => cancelClose(), []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && activeMenu) {
      const ref = triggerRefs.current[activeMenu];
      setActiveMenu(null);
      ref?.focus();
    }
  };

  return (
    <div
      className="hidden md:flex"
      onMouseLeave={scheduleClose}
      onMouseEnter={cancelClose}
      onKeyDown={onKeyDown}
    >
      <ul className="flex items-center gap-1">
        {items.map((item) => {
          const active = isActive(item.href);
          const open = activeMenu === item.href;
          return (
            <li key={item.href}>
              <Link
                ref={(el) => { triggerRefs.current[item.href] = el; }}
                to={item.href}
                aria-haspopup="true"
                aria-expanded={open}
                onMouseEnter={() => openMenu(item.href)}
                onFocus={() => openMenu(item.href)}
                className={cn(
                  "group inline-flex items-center gap-1 rounded-md px-3 py-2 text-[13px] font-medium transition-colors outline-none",
                  dark
                    ? active || open
                      ? "text-seabed-foreground"
                      : "text-seabed-foreground/70 hover:text-seabed-foreground"
                    : active || open
                      ? "text-foreground"
                      : "text-foreground/65 hover:text-foreground",
                )}
              >
                {item.label}
                <span
                  aria-hidden
                  className={cn(
                    "ml-0.5 h-1 w-1 rounded-full bg-primary transition-opacity",
                    open ? "opacity-100" : "opacity-0",
                  )}
                />
              </Link>
            </li>
          );
        })}
      </ul>

      <AnimatePresence>
        {activeSection && (
          <motion.div
            key="mega-panel"
            initial={{ opacity: 0, x: "-50%", y: -4, scale: 0.98 }}
            animate={{ opacity: 1, x: "-50%", y: 0, scale: 1 }}
            exit={{ opacity: 0, x: "-50%", y: -4, scale: 0.98 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute left-1/2 top-full z-10"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            role="region"
            aria-label={activeSection.label}
          >
            {/* Invisible bridge so diagonal cursor moves don't leave the hover region */}
            <div aria-hidden className="h-3 w-full" />
            <motion.div layout transition={{ duration: 0.2, ease: "easeOut" }} className="origin-top">
              <PanelContent key={activeSection.href} section={activeSection} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MegaMenu;
